import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ChatMessage, ChatState } from '../types/chat';
import { chatApi, BackendMessage } from '../services/chatApi';
import { ChatbotPersonality } from '../types/chatbot';
import ChatMessageComponent from './ui/ChatMessage';
import ChatInput from './ui/ChatInput';
import LoadingScreen from './ui/LoadingScreen';

import { useAuth } from '../contexts/AuthContext';
import { cacheService } from '../services/cacheService';

interface ChatInterfaceProps {
  selectedChatbot: ChatbotPersonality;
  conversationId?: string | null;
  onToggleProfile?: () => void;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ selectedChatbot, conversationId, onToggleProfile }) => {
  const [chatState, setChatState] = useState<ChatState>({
    messages: [],
    isLoading: false,
    error: null,
  });
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [lastMessageTime, setLastMessageTime] = useState<number>(0);

  const { user } = useAuth();
  const isDemoUser = (user?.email === 'user@lumyno.us') || (user?.id === '6b2bc579-ac82-432c-ad6f-c3d069f282dc');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef<ChatMessage[]>([]);
  const processingAssistantQueueRef = useRef<boolean>(false);

  // Message identity helpers
  const strictKey = useCallback((m: ChatMessage) =>
    (m.role === 'user' ? `${m.role}|${m.content}` : `${m.role}|${m.content}|${m.timestamp ?? ''}`)
  , []);
  const looseContentKey = useCallback((m: ChatMessage) => `${m.role}|${m.content}` , []);

  // Merge helper to prevent user-message flicker and preserve local messages
  const mergeMessages = useCallback((
    existing: ChatMessage[],
    incoming: ChatMessage[]
  ): ChatMessage[] => {
    const seen = new Set(existing.map(strictKey));
    const merged: ChatMessage[] = [...existing];
    for (const msg of incoming) {
      const sig = strictKey(msg);
      if (!seen.has(sig)) {
        merged.push(msg);
        seen.add(sig);
      }
    }
    // Attempt to keep chronological order when timestamps exist
    const hasTimestamps = merged.every(m => !!m.timestamp);
    if (hasTimestamps) {
      merged.sort((a, b) => (new Date(a.timestamp as string).getTime() - new Date(b.timestamp as string).getTime()));
    }
    return merged;
  }, [strictKey]);

  // Queue and typing management for assistant messages
  const [pendingAssistantQueue, setPendingAssistantQueue] = useState<ChatMessage[]>([]);
  const pendingAssistantQueueRef = useRef<ChatMessage[]>([]);
  const [isAssistantTyping, setIsAssistantTyping] = useState<boolean>(false);

  useEffect(() => {
    messagesRef.current = chatState.messages;
  }, [chatState.messages]);

  useEffect(() => {
    pendingAssistantQueueRef.current = pendingAssistantQueue;
  }, [pendingAssistantQueue]);

  // Debug: print queue state whenever it changes
  useEffect(() => {
    try {
      const summary = pendingAssistantQueue.map((m, idx) => ({ idx, role: m.role, len: m.content.length, ts: m.timestamp }));
      console.log('🧾 Pending assistant queue updated:', { size: pendingAssistantQueue.length, summary });
    } catch {}
  }, [pendingAssistantQueue]);

  const calculateTypingDelayMs = (content: string): number => {
    // Significantly faster typing for demo user to reduce delays
    const charsPerMinute = isDemoUser ? 3000 : 1140; // 3x faster for demo user
    const msPerChar = (60_000 / charsPerMinute);
    const delay = Math.max(100, Math.round(content.length * msPerChar)); // Minimum 100ms
    return Math.min(delay, isDemoUser ? 1000 : 3000); // Cap at 1s for demo user, 3s for others
  };

  const processAssistantQueue = async () => {
    if (processingAssistantQueueRef.current) return;
    processingAssistantQueueRef.current = true;
    try {
      // eslint-disable-next-line no-constant-condition
      while (pendingAssistantQueueRef.current.length > 0) {
        const next = pendingAssistantQueueRef.current[0];
        setIsAssistantTyping(true);
        const delayMs = calculateTypingDelayMs(next.content);
        console.log('⏳ Typing assistant message...', {
          queueSize: pendingAssistantQueueRef.current.length,
          contentLength: next.content.length,
          delayMs,
        });
        
        // For demo user, reduce delay significantly for better UX
        const actualDelay = isDemoUser ? Math.min(delayMs, 300) : delayMs;
        await new Promise((res) => setTimeout(res, actualDelay));

        // Append this message to displayed messages if not present
        setChatState((prev) => ({
          ...prev,
          messages: mergeMessages(prev.messages, [next]),
        }));

        // Pop from queue
        const remaining = Math.max(0, pendingAssistantQueueRef.current.length - 1);
        // Update ref synchronously to avoid stale length during the loop
        pendingAssistantQueueRef.current = pendingAssistantQueueRef.current.slice(1);
        setPendingAssistantQueue((prev) => prev.slice(1));
        console.log('✅ Revealed assistant message. Remaining in queue:', remaining);
      }
    } finally {
      // Ensure both state and refs reflect an empty/idle queue
      const actuallyEmpty = pendingAssistantQueueRef.current.length === 0;
      if (actuallyEmpty) {
        setIsAssistantTyping(false);
      }
      processingAssistantQueueRef.current = false;
      // If new items arrived during finalization, process again
      if (pendingAssistantQueueRef.current.length > 0) {
        setIsAssistantTyping(true);
        processAssistantQueue();
      }
    }
  };

  useEffect(() => {
    if (!processingAssistantQueueRef.current && pendingAssistantQueue.length > 0) {
      processAssistantQueue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAssistantQueue.length]);

  // Debug logging for loading states
  useEffect(() => {
    console.log('🔍 ChatInterface loading states:', { 
      initialLoading, 
      messagesCount: chatState.messages.length,
      conversationId,
      chatbotId: selectedChatbot?.id 
    });
  }, [initialLoading, chatState.messages.length, conversationId, selectedChatbot?.id]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatState.messages]);

  useEffect(() => {
    const mapBackendMessages = (messages: BackendMessage[]): ChatMessage[] =>
      messages.map((m) => ({
        role: m.sender_type === 'user' ? 'user' : 'assistant',
        content: m.content,
        timestamp: m.timestamp,
      }));

    const loadConversation = async () => {
      try {
        setInitialLoading(true);
        
        if (conversationId) {
          // Check if we have cached data synchronously for instant loading
          const syncCached = cacheService.getChatMessages(conversationId);
          if (syncCached && syncCached.messages) {
            console.log('🚀 Instant load from cache');
            setChatState({
              messages: mapBackendMessages(syncCached.messages),
              isLoading: false,
              error: null,
            });
            setCurrentConversationId(syncCached.chat_id);
            setInitialLoading(false);
            return;
          }

          const data = await chatApi.getChatMessages(conversationId);
          setChatState({
            messages: mapBackendMessages(data.messages),
            isLoading: false,
            error: null,
          });
          setCurrentConversationId(data.chat_id);
          setInitialLoading(false);
          return;
        }

        if (selectedChatbot?.id) {
          // Ensure a chat exists with this companion and load its messages
          const ensured = await chatApi.ensureChatWithCompanion(String(selectedChatbot.id));
          setChatState({
            messages: mapBackendMessages(ensured.messages),
            isLoading: false,
            error: null,
          });
          setCurrentConversationId(ensured.chat_id);
          setInitialLoading(false);
          return;
        }

        // Fallback: show welcome message if available
        if (selectedChatbot?.welcomeMessage) {
          setChatState({
            messages: [
              {
                role: 'assistant',
                content: selectedChatbot.welcomeMessage,
                timestamp: new Date().toISOString(),
              },
            ],
            isLoading: false,
            error: null,
          });
        } else {
          setChatState({ messages: [], isLoading: false, error: null });
        }
        setCurrentConversationId(null);
        setInitialLoading(false);
      } catch (error) {
        console.warn('Failed to load chat messages:', error);
        // Fallback to a local greeting so the user sees an opening message
        if (selectedChatbot?.welcomeMessage) {
          setChatState({
            messages: [
              {
                role: 'assistant',
                content: selectedChatbot.welcomeMessage,
                timestamp: new Date().toISOString(),
              },
            ],
            isLoading: false,
            error: null,
          });
        } else {
          setChatState({ messages: [], isLoading: false, error: null });
        }
        setCurrentConversationId(null);
        setInitialLoading(false);
      }
    };

    loadConversation();
  }, [conversationId, selectedChatbot]);

  // Poll for new messages every 500ms for the demo user (faster response)
  useEffect(() => {
    if (!isDemoUser || !currentConversationId) return;
    const intervalId = setInterval(async () => {
      try {
        // Bypass cache so we actually see new messages inserted externally
        const data = await chatApi.getChatMessages(currentConversationId, false);
        const mapBackendMessages = (messages: BackendMessage[]): ChatMessage[] =>
          messages.map((m) => ({
            role: m.sender_type === 'user' ? 'user' : 'assistant',
            content: m.content,
            timestamp: m.timestamp,
          }));
        const incoming = mapBackendMessages(data.messages);
        // Determine new messages relative to what is currently displayed or already queued
        const displayed = new Set(messagesRef.current.map(strictKey));
        const queueStrict = new Set(pendingAssistantQueueRef.current.map(strictKey));
        const queueLoose = new Set(pendingAssistantQueueRef.current.map(looseContentKey));
        const newOnes = incoming.filter((m) => {
          const k = strictKey(m);
          if (displayed.has(k)) return false;
          if (m.role === 'assistant') {
            if (queueStrict.has(k) || queueLoose.has(looseContentKey(m))) return false;
          }
          return true;
        });
        if (newOnes.length > 0) {
          const newUserMessages = newOnes.filter((m) => m.role === 'user');
          const newAssistantMessages = newOnes.filter((m) => m.role === 'assistant');
          console.log('📥 Incoming messages (from server):', {
            totalNew: newOnes.length,
            user: newUserMessages.length,
            assistant: newAssistantMessages.length,
          });
          if (newUserMessages.length > 0) {
            setChatState((prev) => ({ ...prev, messages: mergeMessages(prev.messages, newUserMessages) }));
          }
          if (newAssistantMessages.length > 0) {
            // Filter out any duplicates already in queue by strict or loose keys
            const queueStrictNow = new Set(pendingAssistantQueueRef.current.map(strictKey));
            const queueLooseNow = new Set(pendingAssistantQueueRef.current.map(looseContentKey));
            const toEnqueue = newAssistantMessages.filter((m) => {
              const k = strictKey(m);
              return !queueStrictNow.has(k) && !queueLooseNow.has(looseContentKey(m));
            });
            if (toEnqueue.length !== newAssistantMessages.length) {
              console.log('🧹 Dedup assistant enqueue (poll):', {
                incoming: newAssistantMessages.length,
                enqueued: toEnqueue.length,
              });
            }
            if (toEnqueue.length > 0) setPendingAssistantQueue((prev) => [...prev, ...toEnqueue]);
          }
        }
      } catch (e) {
        // ignore polling errors
      }
    }, 500); // Reduced from 2000ms to 500ms for faster updates
    return () => clearInterval(intervalId);
  }, [isDemoUser, currentConversationId, strictKey, looseContentKey, mergeMessages]);

  const handleSendMessage = async (message: string) => {
    if (chatState.isLoading) return;
    
    // Prevent rapid message sending (debounce)
    const now = Date.now();
    if (now - lastMessageTime < 500) return; // 500ms debounce
    setLastMessageTime(now);

    const userMessage: ChatMessage = {
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
    };

    // Add user message immediately for instant feedback
    setChatState(prev => ({
      ...prev,
      messages: [...prev.messages, userMessage],
      isLoading: true,
      error: null,
    }));

    try {
      // Ensure a chat exists (AI greets first when creating new). If ensure fails, fall back to user-first flow.
      let chatId = currentConversationId;
      if (!chatId) {
        try {
          const ensured = await chatApi.ensureChatWithCompanion(String(selectedChatbot.id));
          chatId = ensured.chat_id;
          setCurrentConversationId(chatId);
          // Greeting was already loaded on mount; avoid re-appending here.
        } catch (ensureError) {
          // Backend ensure failed (e.g., timestamp validation bug). Fallback: create via user-first flow.
          const created = await chatApi.createChat({
            message: userMessage.content,
            companion_id: String(selectedChatbot.id),
            title: selectedChatbot.name ? `Chat with ${selectedChatbot.name}` : undefined,
          });
          chatId = created.chat.chat_id;
          setCurrentConversationId(chatId);
          const assistantReplies: ChatMessage[] = created.messages
            .filter((m) => m.sender_type === 'companion')
            .map((m) => ({ role: 'assistant' as const, content: m.content, timestamp: m.timestamp }));
          // Queue assistant replies instead of immediate append for typing effect
          const queueStrictNow = new Set(pendingAssistantQueueRef.current.map(strictKey));
          const queueLooseNow = new Set(pendingAssistantQueueRef.current.map(looseContentKey));
          const toEnqueue = assistantReplies.filter((m) => {
            const k = strictKey(m);
            return !queueStrictNow.has(k) && !queueLooseNow.has(looseContentKey(m));
          });
          console.log('📥 Enqueue assistant replies (create flow):', { count: toEnqueue.length });
          if (toEnqueue.length > 0) setPendingAssistantQueue((prev) => [...prev, ...toEnqueue]);
          setChatState((prev) => ({ ...prev, isLoading: false, error: null }));
          return;
        }
      }

      // Send a message in the chat
      if (isDemoUser) {
        // Send message without waiting for response to improve perceived speed
        chatApi.sendMessageNoReply(chatId as string, userMessage.content).catch(console.error);
        
        // Invalidate cache since we have new messages
        cacheService.invalidate(`chat_messages_${chatId}`);
        
        // End loading immediately - polling will pick up the response quickly
        setChatState((prev) => ({
          ...prev,
          isLoading: false,
          error: null,
        }));
      } else {
        const sent = await chatApi.sendMessage(chatId as string, userMessage.content);
        const assistantReplies: ChatMessage[] = sent.messages
          .filter((m) => m.sender_type === 'companion')
          .map((m) => ({
            role: 'assistant' as const,
            content: m.content,
            timestamp: m.timestamp,
          }));

        // Queue assistant replies for typing effect
        if (assistantReplies.length > 0) {
          const queueStrictNow = new Set(pendingAssistantQueueRef.current.map(strictKey));
          const queueLooseNow = new Set(pendingAssistantQueueRef.current.map(looseContentKey));
          const toEnqueue = assistantReplies.filter((m) => {
            const k = strictKey(m);
            return !queueStrictNow.has(k) && !queueLooseNow.has(looseContentKey(m));
          });
          console.log('📥 Enqueue assistant replies (send flow):', { count: toEnqueue.length });
          if (toEnqueue.length > 0) setPendingAssistantQueue((prev) => [...prev, ...toEnqueue]);
        }
        setChatState((prev) => ({ ...prev, isLoading: false, error: null }));
      }
    } catch (error) {
      setChatState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to send message',
      }));
    }
  };



  // Show loading screen while initially loading conversation
  if (initialLoading) {
    return (
      <div className="flex flex-col h-full relative overflow-hidden">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 glass-effect rounded-2xl border border-dark-600 flex-1 flex flex-col overflow-hidden h-full"
        >
          <div className="flex-1 flex items-center justify-center">
            <LoadingScreen
              title="Loading Chat"
              message={conversationId ? "Loading conversation history..." : "Setting up your chat..."}
            />
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full relative overflow-hidden">

      {/* Unified Chat Container */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 glass-effect rounded-2xl border border-dark-600 flex-1 flex flex-col overflow-hidden h-full"
      >
        {/* Header */}
        <div className="p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              {/* Chatbot Avatar */}
              <div className="relative flex-shrink-0">
                {selectedChatbot.avatar ? (
                  <img
                    src={selectedChatbot.avatar}
                    alt={selectedChatbot.name}
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover border-2 border-green-500"
                  />
                ) : (
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-r from-accent-purple to-accent-pink flex items-center justify-center">
                    <span className="text-white text-lg sm:text-xl">🤖</span>
                  </div>
                )}
                <div className="absolute -bottom-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 bg-green-500 rounded-full border-2 border-dark-900 flex items-center justify-center">
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full animate-pulse"></div>
                </div>
              </div>

              {/* Chatbot Info */}
              <div className="min-w-0 flex-1">
                <h3 className="text-lg sm:text-xl font-bold text-white truncate">
                  {selectedChatbot.name}
                </h3>
              </div>
            </div>

            {/* Status and Actions */}
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              {/* Info button to toggle profile */}
              {onToggleProfile && (
                <button
                  onClick={onToggleProfile}
                  className="w-8 h-8 bg-accent-purple/20 hover:bg-accent-purple/30 text-accent-purple rounded-full flex items-center justify-center transition-all duration-300 hover:scale-105"
                  title="View profile"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
              )}

            </div>
          </div>
        </div>

        {/* Separator Line */}
        <div className="border-t border-dark-600"></div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-3 sm:space-y-4">
          {chatState.messages.length === 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-12"
            >
              <div className="w-16 h-16 bg-gradient-to-r from-accent-purple to-accent-pink rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🤖</span>
              </div>
              <h4 className="text-lg font-semibold text-white mb-2">Start a conversation</h4>
              <p className="text-dark-300 max-w-md mx-auto">
                Say hello to {selectedChatbot.name} and begin your chat experience!
              </p>
            </motion.div>
          )}

          {chatState.messages.map((message, index) => (
            <ChatMessageComponent
              key={index}
              message={message.content}
              sender={message.role === 'user' ? 'user' : 'bot'}
              timestamp={message.timestamp ? new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : undefined}
              avatar={message.role === 'assistant' ? selectedChatbot.avatar : undefined}
            />
          ))}

          {isAssistantTyping && pendingAssistantQueue.length > 0 && (
            <ChatMessageComponent
              message=""
              sender="bot"
              isTyping={true}
              avatar={selectedChatbot.avatar}
            />
          )}

          {chatState.error && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex justify-center"
            >
              <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl text-sm max-w-md text-center">
                <div className="flex items-center gap-2 justify-center mb-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="font-medium">Error</span>
                </div>
                {chatState.error}
              </div>
            </motion.div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-6 border-t border-dark-600">
          <ChatInput
            onSendMessage={handleSendMessage}
            placeholder={`Message ${selectedChatbot.name}...`}
            disabled={chatState.isLoading}
          />
        </div>
      </motion.div>
    </div>
  );
};

export default ChatInterface;